import React, { useEffect, useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import { loginAsyncThunk, verifyOtpAsyncThunk, updateProfileAsyncThunk, clearLoginState, clearLoginOptions, getLoginOptionsAsyncThunk, setMaskedTarget, setOtpMethod } from '../store/auth/authSlice';
import { Toast } from 'primereact/toast';
import { useThemeContext } from '../context/useThemeContext';
import { Dropdown } from 'primereact/dropdown';
import { getCompaniesByUserAsyncThunk, setSelectCompany } from '../store/company/userCompanySlice';
import { removeToken, removeUser, removeCompanyId } from '../service/tokenServices';
import { setStoreUser, setIsAuthenticated } from '../store/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { InputMask } from 'primereact/inputmask';
import { RadioButton } from 'primereact/radiobutton';

// Telefon Güncelleme Dialog'u
function PhoneUpdateDialog({ visible, onHide, toast }) {
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const inputMaskRef = useRef(null);

    // autoFocus yerine useEffect kullanarak focus yap (InputMask bug fix)
    useEffect(() => {
        if (visible && inputMaskRef.current) {
            setTimeout(() => {
                inputMaskRef.current?.focus();
            }, 100);
        }
    }, [visible]);

    const handleSubmit = async () => {
        if (!phone || phone.includes('_')) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen geçerli bir telefon numarası giriniz.' });
            return;
        }

        setLoading(true);
        try {
            const cleanPhone = phone.replace(/\s/g, '');
            const result = await dispatch(updateProfileAsyncThunk({ phone: cleanPhone }));

            if (result.type === 'auth/updateProfile/fulfilled') {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Telefon numarası güncellendi. Lütfen tekrar giriş yapınız.' });
                // Logout yap - kullanıcı tekrar login yapacak
                dispatch(clearLoginState());
                removeToken();
                removeUser();
                dispatch(setStoreUser(null));
                dispatch(setIsAuthenticated(false));
                setPhone('');
                onHide();
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Telefon güncellenirken bir hata oluştu.' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Telefon güncellenirken bir hata oluştu.' });
        }
        setLoading(false);
    };

    const handleCancel = () => {
        dispatch(clearLoginState());
        removeToken();
        removeUser();
        dispatch(setStoreUser(null));
        dispatch(setIsAuthenticated(false));
        setPhone('');
        onHide();
    };

    return (
        <Dialog
            header="Telefon Numarası Gerekli"
            visible={visible}
            style={{ width: '400px' }}
            modal
            closable={false}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button label="İptal" icon="pi pi-times" className="p-button-text" onClick={handleCancel} />
                    <Button label="Kaydet" icon="pi pi-check" loading={loading} onClick={handleSubmit} />
                </div>
            }
        >
            <div className="flex flex-column gap-3">
                <p className="m-0">Devam etmek için telefon numaranızı girmeniz gerekmektedir. Güncelleme sonrası tekrar giriş yapmanız gerekecektir.</p>
                <div className="p-inputgroup">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-phone"></i>
                    </span>
                    <InputMask ref={inputMaskRef} value={phone} onChange={(e) => setPhone(e.value)} onKeyDown={(e) => e.key === 'Enter' && !loading && handleSubmit()} mask="0599 999 99 99" placeholder="05XX XXX XX XX" className="w-full" />
                </div>
            </div>
        </Dialog>
    );
}

// Şifre validasyon fonksiyonu
const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    return {
        minLength,
        hasLowercase,
        hasUppercase,
        hasNumber,
        hasSpecial,
        isValid: minLength && hasLowercase && hasUppercase && hasNumber && hasSpecial
    };
};

// Şifre Değiştirme Dialog'u
function PasswordChangeDialog({ visible, onHide, toast }) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const passwordValidation = validatePassword(password);

    const handleSubmit = async () => {
        if (!passwordValidation.isValid) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Şifre gereksinimleri karşılanmıyor.' });
            return;
        }

        if (password !== confirmPassword) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Şifreler eşleşmiyor.' });
            return;
        }

        setLoading(true);
        try {
            const result = await dispatch(updateProfileAsyncThunk({ password }));

            if (result.type === 'auth/updateProfile/fulfilled') {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Şifre güncellendi. Lütfen yeni şifrenizle tekrar giriş yapınız.' });
                // Logout yap - kullanıcı tekrar login yapacak
                dispatch(clearLoginState());
                removeToken();
                removeUser();
                dispatch(setStoreUser(null));
                dispatch(setIsAuthenticated(false));
                setPassword('');
                setConfirmPassword('');
                onHide();
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Şifre güncellenirken bir hata oluştu.' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Şifre güncellenirken bir hata oluştu.' });
        }
        setLoading(false);
    };

    const handleCancel = () => {
        dispatch(clearLoginState());
        removeToken();
        removeUser();
        dispatch(setStoreUser(null));
        dispatch(setIsAuthenticated(false));
        setPassword('');
        setConfirmPassword('');
        onHide();
    };

    return (
        <Dialog
            header="Şifre Değişikliği Gerekli"
            visible={visible}
            style={{ width: '400px' }}
            modal
            closable={false}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button label="İptal" icon="pi pi-times" className="p-button-text" onClick={handleCancel} />
                    <Button label="Kaydet" icon="pi pi-check" loading={loading} onClick={handleSubmit} />
                </div>
            }
        >
            <div className="flex flex-column gap-3">
                <p className="m-0">Güvenliğiniz için şifrenizi değiştirmeniz gerekmektedir. Güncelleme sonrası yeni şifrenizle tekrar giriş yapmanız gerekecektir.</p>

                {/* Yeni Şifre */}
                <div className="p-inputgroup">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-lock"></i>
                    </span>
                    <InputText type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Yeni Şifre" className="w-full" autoFocus />
                    <span className="p-inputgroup-addon cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                        <i className={showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'}></i>
                    </span>
                </div>

                {/* Şifre Tekrar */}
                <div className="p-inputgroup">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-lock"></i>
                    </span>
                    <InputText
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !loading && handleSubmit()}
                        placeholder="Şifre Tekrar"
                        className="w-full"
                    />
                    <span className="p-inputgroup-addon cursor-pointer" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        <i className={showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'}></i>
                    </span>
                </div>

                {/* Şifre Gereksinimleri */}
                <div className="text-sm">
                    <p className="m-0 mb-2 font-semibold">Şifre Gereksinimleri:</p>
                    <ul className="m-0 pl-4" style={{ listStyle: 'none' }}>
                        <li className={passwordValidation.minLength ? 'text-green-500' : 'text-red-500'}>
                            <i className={`pi ${passwordValidation.minLength ? 'pi-check' : 'pi-times'} mr-2`}></i>
                            En az 8 karakter
                        </li>
                        <li className={passwordValidation.hasLowercase ? 'text-green-500' : 'text-red-500'}>
                            <i className={`pi ${passwordValidation.hasLowercase ? 'pi-check' : 'pi-times'} mr-2`}></i>
                            En az 1 küçük harf (a-z)
                        </li>
                        <li className={passwordValidation.hasUppercase ? 'text-green-500' : 'text-red-500'}>
                            <i className={`pi ${passwordValidation.hasUppercase ? 'pi-check' : 'pi-times'} mr-2`}></i>
                            En az 1 büyük harf (A-Z)
                        </li>
                        <li className={passwordValidation.hasNumber ? 'text-green-500' : 'text-red-500'}>
                            <i className={`pi ${passwordValidation.hasNumber ? 'pi-check' : 'pi-times'} mr-2`}></i>
                            En az 1 rakam (0-9)
                        </li>
                        <li className={passwordValidation.hasSpecial ? 'text-green-500' : 'text-red-500'}>
                            <i className={`pi ${passwordValidation.hasSpecial ? 'pi-check' : 'pi-times'} mr-2`}></i>
                            En az 1 özel karakter (!@#$%^&* vb.)
                        </li>
                    </ul>
                </div>
            </div>
        </Dialog>
    );
}

// OTP Yöntemi Seçim Dialog'u
function OtpMethodSelectionDialog({ visible, onHide, onSelect, toast, options }) {
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [loading, setLoading] = useState(false);

    // Dialog açıldığında varsayılan seçimi ayarla
    useEffect(() => {
        if (visible && options && options.length > 0) {
            const phoneOption = options.find((opt) => opt.method === 'phone' && opt.available);
            const emailOption = options.find((opt) => opt.method === 'email' && opt.available);
            // Varsayılan olarak telefon seçili olsun (varsa), yoksa email
            setSelectedMethod(phoneOption ? 'phone' : emailOption ? 'email' : null);
        }
    }, [visible, options]);

    const handleSubmit = () => {
        if (!selectedMethod) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen bir doğrulama yöntemi seçiniz.' });
            return;
        }
        setLoading(true);
        onSelect(selectedMethod);
        setLoading(false);
    };

    const handleCancel = () => {
        setSelectedMethod(null);
        onHide();
    };

    const getMethodLabel = (method) => {
        return method === 'phone' ? 'Telefon (SMS)' : 'E-posta';
    };

    const getMethodIcon = (method) => {
        return method === 'phone' ? 'pi pi-mobile' : 'pi pi-envelope';
    };

    return (
        <Dialog
            header="Doğrulama Yöntemi Seçin"
            visible={visible}
            style={{ width: '400px' }}
            modal
            closable={false}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button label="İptal" icon="pi pi-times" className="p-button-text" onClick={handleCancel} />
                    <Button label="Devam Et" icon="pi pi-check" loading={loading} onClick={handleSubmit} disabled={!selectedMethod} />
                </div>
            }
        >
            <div className="flex flex-column gap-3">
                <p className="m-0">Doğrulama kodunun gönderileceği yöntemi seçiniz:</p>
                <div className="flex flex-column gap-2">
                    {options &&
                        options
                            .filter((opt) => opt.available)
                            .map((option) => (
                                <div
                                    key={option.method}
                                    className={`flex align-items-center p-3 border-round cursor-pointer ${selectedMethod === option.method ? 'bg-primary-100 border-primary' : 'surface-100'}`}
                                    style={{ border: '1px solid var(--surface-300)' }}
                                    onClick={() => setSelectedMethod(option.method)}
                                >
                                    <RadioButton inputId={option.method} name="otpMethod" value={option.method} onChange={(e) => setSelectedMethod(e.value)} checked={selectedMethod === option.method} />
                                    <label htmlFor={option.method} className="ml-3 flex align-items-center cursor-pointer flex-1">
                                        <i className={`${getMethodIcon(option.method)} mr-2`}></i>
                                        <div>
                                            <div className="font-semibold">{getMethodLabel(option.method)}</div>
                                            <div className="text-sm text-500">{option.masked}</div>
                                        </div>
                                    </label>
                                </div>
                            ))}
                </div>
            </div>
        </Dialog>
    );
}

// OTP Doğrulama Dialog'u
function OtpVerificationDialog({ visible, onHide, onPasswordChangeRequired, toast, email, maskedPhone, maskedTarget, otpMethod }) {
    const [otpCode, setOtpCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const dispatch = useDispatch();

    // Dinamik başlık ve mesaj
    const isPhoneOtp = otpMethod === 'phone';
    const dialogHeader = isPhoneOtp ? 'SMS Doğrulama' : 'E-posta Doğrulama';
    const targetLabel = maskedTarget || maskedPhone;
    const targetDescription = isPhoneOtp ? `${targetLabel} numaralı telefonunuza` : `${targetLabel} adresine`;

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleSubmit = async () => {
        if (!otpCode || otpCode.length !== 6) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen 6 haneli doğrulama kodunu giriniz.' });
            return;
        }

        setLoading(true);
        try {
            const result = await dispatch(verifyOtpAsyncThunk({ email, otp_code: otpCode }));

            if (result.type === 'auth/verifyOtp/fulfilled') {
                const { data } = result.payload;

                // Şifre değişikliği gerekiyor mu?
                if (data.requires_password_change) {
                    toast.current.show({ severity: 'info', summary: 'Şifre Değişikliği', detail: 'Güvenliğiniz için şifrenizi değiştirmeniz gerekmektedir.' });
                    setOtpCode('');
                    onHide();
                    onPasswordChangeRequired();
                } else {
                    toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Giriş başarılı.' });
                    setOtpCode('');
                    onHide();
                }
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Doğrulama kodu hatalı.' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Doğrulama kodu hatalı.' });
        }
        setLoading(false);
    };

    const handleCancel = () => {
        dispatch(clearLoginState());
        setOtpCode('');
        onHide();
    };

    const handleResend = async () => {
        // Yeniden login yaparak OTP gönder
        setCountdown(60);
        toast.current.show({ severity: 'info', summary: 'Bilgi', detail: 'Yeni kod gönderildi.' });
    };

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '400px' }}
            modal
            closable={false}
            footer={
                <div className="flex justify-content-between align-items-center">
                    <Button label={countdown > 0 ? `Tekrar Gönder (${countdown}s)` : 'Tekrar Gönder'} icon="pi pi-refresh" className="p-button-text" onClick={handleResend} disabled={countdown > 0} />
                    <div className="flex gap-2">
                        <Button label="İptal" icon="pi pi-times" className="p-button-text" onClick={handleCancel} />
                        <Button label="Doğrula" icon="pi pi-check" loading={loading} onClick={handleSubmit} />
                    </div>
                </div>
            }
        >
            <div className="flex flex-column gap-3">
                <p className="m-0">
                    <strong>{targetDescription}</strong> gönderilen 6 haneli doğrulama kodunu giriniz.
                </p>
                <div className="p-inputgroup">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-lock"></i>
                    </span>
                    <InputText
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={(e) => e.key === 'Enter' && !loading && handleSubmit()}
                        placeholder="000000"
                        className="w-full text-center"
                        style={{ letterSpacing: '0.5rem', fontSize: '1.2rem' }}
                        maxLength={6}
                        autoFocus
                    />
                </div>
            </div>
        </Dialog>
    );
}

function LoginForm() {
    const { isAuthenticated, requiresPhoneUpdate, requiresOtp, requiresPasswordChange, pendingEmail, maskedPhone, maskedTarget, otpMethod, loginOptions } = useSelector((state) => state.auth);
    const toast = useRef(null);
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [pending, setPending] = useState(false);
    const [showPassword, setShowPassword] = useState(true);
    const [showPhoneDialog, setShowPhoneDialog] = useState(false);
    const [showOtpDialog, setShowOtpDialog] = useState(false);
    const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
    const [showOtpMethodDialog, setShowOtpMethodDialog] = useState(false);

    const { colorScheme } = useThemeContext();
    const dispatch = useDispatch();

    // State değişikliklerini izle
    useEffect(() => {
        if (requiresPhoneUpdate) {
            setShowPhoneDialog(true);
        }
    }, [requiresPhoneUpdate]);

    useEffect(() => {
        if (requiresOtp) {
            setShowOtpDialog(true);
        }
    }, [requiresOtp]);

    useEffect(() => {
        if (requiresPasswordChange) {
            setShowPasswordChangeDialog(true);
        }
    }, [requiresPasswordChange]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPending(true);

        // Önce login options kontrolü yap
        const optionsResult = await dispatch(getLoginOptionsAsyncThunk({ email }));

        if (optionsResult.type === 'auth/getLoginOptions/fulfilled') {
            const options = optionsResult.payload.data?.options || [];
            const phoneOption = options.find((opt) => opt.method === 'phone');

            // Telefon mevcut mu kontrol et
            if (phoneOption && phoneOption.available) {
                // Telefon var, kullanıcı seçim yapsın
                setPending(false);
                setShowOtpMethodDialog(true);
            } else {
                // Telefon yok, direkt login yap (backend requires_phone_update dönecek)
                await performLogin(null);
            }
        } else {
            // Options alınamadı, hata göster
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Giriş seçenekleri alınamadı.' });
            setPending(false);
        }
    };

    // Gerçek login işlemi
    const performLogin = async (otpMethod) => {
        setPending(true);
        const control = await dispatch(loginAsyncThunk({ email, password, otp_method: otpMethod }));
        if (control.type === 'auth/login/fulfilled') {
            const { data } = control.payload;

            // OTP veya telefon güncelleme gerekiyorsa, dialog açılacak
            if (!data.requires_otp && !data.requires_phone_update) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Giriş Yapıldı.' });
            } else if (data.requires_otp) {
                toast.current.show({ severity: 'info', summary: 'Doğrulama Kodu Gönderildi', detail: `Doğrulama kodu ${otpMethod === 'phone' ? 'telefonunuza' : 'e-posta adresinize'} gönderildi.` });
            } else if (data.requires_phone_update) {
                toast.current.show({ severity: 'info', summary: 'Telefon Gerekli', detail: 'Devam etmek için telefon numaranızı girmeniz gerekmektedir.' });
            }
        } else if (control.type === 'auth/login/rejected') {
            toast.current.show({ severity: 'error', summary: 'Giriş Başarısız', detail: 'E-Mail veya Şifre Hatalı.' });
        }
        setPending(false);
    };

    // OTP yöntem seçimi callback
    const handleOtpMethodSelect = async (selectedMethod) => {
        setShowOtpMethodDialog(false);

        // Seçilen yöntemin masked değerini bul ve set et
        const selectedOption = loginOptions?.find((opt) => opt.method === selectedMethod);
        if (selectedOption) {
            dispatch(setMaskedTarget(selectedOption.masked));
            dispatch(setOtpMethod(selectedMethod));
        }

        await performLogin(selectedMethod);
    };

    const handleOtpMethodDialogHide = () => {
        setShowOtpMethodDialog(false);
        dispatch(clearLoginOptions());
    };

    const handleClear = () => {
        setEmail('');
        setPassword('');
        removeCompanyId();
        removeToken();
        removeUser();
        dispatch(setStoreUser(null));
        dispatch(setIsAuthenticated(false));
        dispatch(clearLoginState());
    };

    const handlePhoneDialogHide = () => {
        setShowPhoneDialog(false);
    };

    const handleOtpDialogHide = () => {
        setShowOtpDialog(false);
    };

    const handlePasswordChangeDialogHide = () => {
        setShowPasswordChangeDialog(false);
    };

    const handlePasswordChangeRequired = () => {
        setShowPasswordChangeDialog(true);
    };

    return (
        <div>
            <Toast ref={toast} />

            {/* Telefon Güncelleme Dialog */}
            <PhoneUpdateDialog visible={showPhoneDialog} onHide={handlePhoneDialogHide} toast={toast} />

            {/* OTP Yöntemi Seçim Dialog */}
            <OtpMethodSelectionDialog visible={showOtpMethodDialog} onHide={handleOtpMethodDialogHide} onSelect={handleOtpMethodSelect} toast={toast} options={loginOptions} />

            {/* OTP Doğrulama Dialog */}
            <OtpVerificationDialog
                visible={showOtpDialog}
                onHide={handleOtpDialogHide}
                onPasswordChangeRequired={handlePasswordChangeRequired}
                toast={toast}
                email={pendingEmail}
                maskedPhone={maskedPhone}
                maskedTarget={maskedTarget}
                otpMethod={otpMethod}
            />

            {/* Şifre Değiştirme Dialog */}
            <PasswordChangeDialog visible={showPasswordChangeDialog} onHide={handlePasswordChangeDialogHide} toast={toast} />

            <form className="flex flex-column" onSubmit={handleSubmit}>
                <div className="flex align-items-center mb-6 logo-container">
                    <img src={`/assets/layout/images/i-uyum-logo-${colorScheme === 'light' ? 'light' : 'dark'}.png`} className="login-appname w-10rem h-3rem" alt="login-appname" />
                </div>
                <div className="form-container">
                    <span className="p-input-icon-left">
                        <i className="pi pi-envelope"></i>
                        <InputText value={email} onChange={(e) => setEmail(e.target.value)} type="text" placeholder="E-mail" disabled={isAuthenticated} />
                    </span>
                    <span className="p-input-icon-left">
                        <i className="pi pi-key"></i>
                        <InputText value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? 'password' : 'text'} placeholder="Şifre" disabled={isAuthenticated} />
                        <Button
                            type="button"
                            className="p-button-text p-button-sm p-0 text-sky-600 ml-2 absolute top-0 h-full"
                            style={{ left: '20rem' }}
                            icon={showPassword ? 'pi pi-eye' : 'pi pi-eye-slash'}
                            onClick={() => setShowPassword(!showPassword)}
                        />
                    </span>
                </div>
                {isAuthenticated && (
                    <div className="flex w-min">
                        <Button type="button" onClick={() => handleClear()} label="Temizle" icon="pi pi-eraser" severity="success" className="text-sm text-white" />
                    </div>
                )}
                <div className="button-container mt-4">
                    <Button type="submit" label="Giriş Yap" loading={pending} disabled={isAuthenticated}></Button>
                </div>
            </form>
            <div className="button-container">
                <Button onClick={() => navigate('/forgotPassword')} link label="Şifremi Unuttum" className="text-sm text-sky-600 flex" />
            </div>
        </div>
    );
}

function SelectCompanyDropDown() {
    const { isAuthenticated } = useSelector((state) => state.auth);
    const { companyList } = useSelector((state) => state.userCompany);

    const [selectedCompany, setSelected] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const toast = useRef(null);

    const dispatch = useDispatch();

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(getCompaniesByUserAsyncThunk());
        }
    }, [isAuthenticated, dispatch]);

    useEffect(() => {
        if (companyList.length > 0) {
            getCompany();
        }
    }, [companyList]);

    const getCompany = () => {
        if (companyList.length > 0) {
            let copy = [];

            companyList.forEach((company) => {
                copy.push({ name: company.name, value: company.id });
            });

            setCompanyOptions(copy);
        }
    };

    const handleSelectCompany = (e) => {
        setSelected(e.value);
        let company = companyList.find((company) => company.id === e.value);

        for (let i = 0; i < company.modules.length; i++) {
            if (company.modules[i].setting === null) {
                setSelected(null);
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Şirketin modülüne program eklenmemiş.' });
                return;
            }
        }

        dispatch(setSelectCompany(company));
    };

    if (companyList.length === 0 || !isAuthenticated) {
        return null;
    }

    return (
        <div className="card flex flex-column w-min p-4">
            <Toast ref={toast} />
            <label className="text-left mb-3">Devam etmek için şirket seçiniz</label>
            <Dropdown filter emptyFilterMessage="Şirket Bulunamadı" value={selectedCompany} onChange={handleSelectCompany} options={companyOptions} optionLabel="name" placeholder="Şirket seçiniz..." className="w-20rem text-left" />
        </div>
    );
}

export const Login = () => {
    const { colorScheme } = useThemeContext();

    return (
        <div className="login-body">
            <div className="login-image">
                <img src={`/assets/layout/images/pages/login-${colorScheme === 'light' ? 'ondark' : 'onlight'}.png`} alt="" />
            </div>
            <div className="login-panel p-fluid flex flex-column">
                <LoginForm />
                <SelectCompanyDropDown />
            </div>
        </div>
    );
};
